import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function OnboardSponsorsPage() {
  const pageRef = useRef(null);

  // Grouped and ordered strictly by official pricing matrix prominence
  const sponsorTiers = [
    {
      tierId: "co-host",
      tierName: "Co-Host",
      description: "Principal Event Partner",
      gridClass: "grid-cols-1",
      cardClass: "max-w-xl mx-auto p-8 border-t-4 border-[#0a1d37]",
      accent: "bg-[#0a1d37] text-white",
      sponsors: [
        {
          id: "msrdc",
          name: "MSRDC",
          url: "https://msrdc.in/1307/Home",
          logo: "/gris2027sponsors/MSRDC Logo.jpeg"
        }
      ]
    },
    {
      tierId: "platinum",
      tierName: "Platinum Partner",
      description: "Premier Strategic Partner",
      gridClass: "grid-cols-1 md:grid-cols-2",
      cardClass: "max-w-md mx-auto lg:max-w-none p-8 border-t-4 border-slate-500",
      accent: "bg-slate-500 text-white",
      sponsors: [
        {
          id: "irb",
          name: "IRB Infrastructure Developers",
          url: "https://www.irb.co.in/home/",
          logo: "/gris2027sponsors/IRB Logo.jpeg"
        },
        {
          id: "navayuga",
          name: "Navayuga Engineering Company Ltd.",
          url: "https://www.navayuga.com/", 
          logo: "/gris2027sponsors/navyuga.png"
        }
      ]
    },
    {
      tierId: "diamond",
      tierName: "Diamond Partner",
      description: "Key Industry Partner",
      gridClass: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
      cardClass: "max-w-md mx-auto lg:max-w-none p-8 border-t-4 border-cyan-600",
      accent: "bg-cyan-600 text-white",
      sponsors: [
        {
          id: "dra",
          name: "DRA Infracon",
          url: "https://draipl.com/",
          logo: "/gris2027sponsors/DRA Logo.jpeg"
        }
      ]
    },
    {
      tierId: "gold",
      tierName: "Gold Partner",
      description: "Key Gold Partner",
      gridClass: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
      cardClass: "max-w-md mx-auto lg:max-w-none p-8 border-t-4 border-amber-500",
      accent: "bg-amber-500 text-white",
      sponsors: [
        {
          id: "montecarlo",
          name: "Montecarlo Limited",
          url: "https://mclindia.com/",
          logo: "/gris2027sponsors/montecarlolimited.jpg"
        }
      ]
    },
    {
      tierId: "silver",
      tierName: "Silver Partner",
      description: "Key Silver Partner",
      gridClass: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
      cardClass: "max-w-md mx-auto lg:max-w-none p-8 border-t-4 border-slate-400",
      accent: "bg-slate-400 text-white",
      sponsors: [
        {
          id: "dcc-infra",
          name: "DCC Infra Projects Ltd.",
          url: "https://dccinfra.co.in/",
          logo: "/gris2027sponsors/DCC Infra Projects Ltd.png"
        }
      ]
    }
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-text", { y: 40, opacity: 0, duration: 1, ease: "power3.out", stagger: 0.2 });
      
      // Animate each tier section sequentially
      gsap.utils.toArray(".tier-section").forEach((section) => {
        gsap.fromTo(section, 
          { opacity: 0, y: 50 },
          { opacity: 1, y: 0, duration: 1, ease: "power2.out", scrollTrigger: { trigger: section, start: "top 85%" } }
        );
      });
    }, pageRef);
    
    return () => ctx.revert();
  }, []);

  return (
    <main className="bg-slate-50 min-h-screen pb-20 font-sans text-slate-800" ref={pageRef}>
      
      {/* REACT 19 NATIVE SEO HOISTING */}
      <title>Our Sponsors | GRIS 2027</title>
      <meta name="description" content="Discover the industry leaders, co-hosts, and corporate sponsors supporting the Global Road Infratech Summit (GRIS) 2027." />
      <meta name="keywords" content="GRIS 2027 Sponsors, MSRDC, IRB Infrastructure, Navayuga Engineering, DRA, Montecarlo Limited, DCC Infra Projects Ltd, Road Summit Partners" />
      <meta property="og:title" content="Our Sponsors | GRIS 2027" />
      
      {/* 1. CINEMATIC HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-[#0a1d37]">
        <div className="absolute inset-0 z-0">
          <img 
            src="/hero-road-3.jpg" 
            alt="GRIS 2027 Sponsors and Partners Layout" 
            fetchPriority="high" 
            decoding="async"
            className="w-full h-full object-cover object-top opacity-20 mix-blend-luminosity" 
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1d37]/90 via-[#0a1d37]/80 to-slate-50"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center flex flex-col items-center">
          <span className="hero-text inline-block px-5 py-2 bg-orange-500/20 border border-orange-500/50 text-orange-400 rounded-full text-[10px] md:text-xs font-black uppercase tracking-widest mb-6 backdrop-blur-sm">
            Industry Leaders
          </span>
          <h1 className="hero-text text-4xl md:text-6xl lg:text-[5rem] font-black text-white tracking-tight leading-none mb-4 uppercase">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-[#FFB067]">Sponsors</span>
          </h1>
          <p className="hero-text mt-4 text-lg md:text-xl text-slate-300 max-w-2xl mx-auto font-medium leading-relaxed">
            Proudly supported by organizations driving innovation, safety, and sustainability in road infrastructure.
          </p>
        </div>
      </section>

      {/* 2. TIERED SPONSORS HIERARCHY */}
      <section className="py-12 lg:py-20 max-w-7xl mx-auto px-6 lg:px-8 -mt-10 relative z-20 space-y-20 lg:space-y-32">
        
        {sponsorTiers.map((tier) => (
          <div key={tier.tierId} className="tier-section">
            
            {/* Tier Header */}
            <div className="text-center mb-10 md:mb-14 flex flex-col items-center">
              <span className={`px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-4 shadow-sm ${tier.accent}`}>
                {tier.tierName}
              </span>
              <div className="w-16 h-1 bg-slate-200 rounded-full mb-4"></div>
              <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">{tier.description}</p>
            </div>

            {/* Tier Grid */}
            <div className={`grid gap-8 ${tier.gridClass}`}>
              {tier.sponsors.map((sponsor) => (
                <div 
                  key={sponsor.id} 
                  className={`bg-white rounded-[2rem] shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 flex flex-col items-center text-center group ${tier.cardClass}`}
                >
                  {/* STRICTLY UNIFORM LOGO CONTAINER - Height & Width identical across ALL tiers */}
                  <div className="w-full h-40 md:h-48 flex items-center justify-center p-6 mb-8 bg-slate-50/80 rounded-2xl border border-slate-100 group-hover:border-slate-200 transition-colors">
                    <img 
                      src={sponsor.logo} 
                      alt={`${sponsor.name} Official Logo`} 
                      loading="lazy"
                      // Identical optical boundaries for all logos
                      className="max-h-24 md:max-h-32 max-w-[85%] w-auto h-auto object-contain mix-blend-multiply transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>

                  {/* UNIFORM Title & Link - Exact same font size for all tiers */}
                  <div className="flex flex-col h-full w-full">
                    <h3 className="font-black text-2xl text-[#0a1d37] mb-8 mt-auto px-4 leading-tight">
                      {sponsor.name}
                    </h3>
                    
                    <a 
                      href={sponsor.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      aria-label={`Visit ${sponsor.name} official website`}
                      className="w-full py-4 bg-slate-50 hover:bg-[#0a1d37] text-slate-600 hover:text-white border border-slate-200 hover:border-[#0a1d37] font-bold text-xs uppercase tracking-widest rounded-xl transition-all mt-auto"
                    >
                      Visit Website
                    </a>
                  </div>
                </div>
              ))}
            </div>

          </div>
        ))}
      </section>

      {/* 3. BECOME A SPONSOR CTA */}
      <section className="max-w-4xl mx-auto px-6 lg:px-8 pb-12 tier-section">
        <div className="bg-orange-500 rounded-[2rem] p-8 md:p-12 text-center text-white shadow-lg relative overflow-hidden">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(#fff 1.5px, transparent 1.5px)', backgroundSize: '24px 24px' }}></div>
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-black mb-4 tracking-tight">Partner With Us</h2>
            <p className="text-lg font-medium text-white/90 mb-8 max-w-2xl mx-auto">
              Multiple partnership tiers including Bronze and Delegate Kit sponsorships are still available.
            </p>
            <a 
              href="/sponsorship" 
              className="inline-block bg-[#0a1d37] hover:bg-slate-900 text-white font-black px-8 py-4 rounded-xl text-sm uppercase tracking-widest shadow-xl hover:-translate-y-1 transition-all"
            >
              View Available Packages
            </a>
          </div>
        </div>
      </section>

    </main>
  );
}